import React, { useEffect, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import toast from "react-hot-toast";
import { facilityAdmin } from "../../api";
import BRAND from "../../brand";
import ExportMenu from "./ExportMenu";

// "Trends & Analysis" tab (white-label builds): a date range, busiest weekdays and hours,
// how long clients waited against a wait you choose, skip / abandon / sent-back rates per
// station, and throughput per staff member. Data: /reports/analysis/.

const P = BRAND.colorPrimary;
const A = BRAND.colorAccent;
const BAD = "#DC2626";
const WARN = "#D97706";

const iso = (offset = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
};
const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? "a" : "p"}`;
const mins = (v) => (v == null ? "–" : `${v % 1 === 0 ? v : v.toFixed(1)} min`);
const shortDate = (s) => { const d = new Date(`${s}T00:00:00`); return `${d.getDate()}/${d.getMonth() + 1}`; };

const Card = ({ title, sub, children, style }) => (
  <div style={{ ...S.card, ...style }}>
    <div style={S.cardTitle}>{title}</div>
    {sub && <div style={S.sub}>{sub}</div>}
    {children}
  </div>
);

const Stat = ({ label, value, tint }) => (
  <div style={{ ...S.card, borderTop: `3px solid ${tint}`, padding: "14px 16px" }}>
    <div style={S.sub}>{label}</div>
    <div style={S.big}>{value}</div>
  </div>
);

export default function TrendsAnalysis() {
  const [from, setFrom] = useState(iso(-13));
  const [to, setTo] = useState(iso());
  const [wait, setWait] = useState(15);
  const [zone, setZone] = useState("");
  const [station, setStation] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      facilityAdmin.analysisReport({ date_from: from, date_to: to, wait_minutes: wait || 15, zone: zone || undefined, station: station || undefined })
        .then((r) => { if (live) setD(r.data); })
        .catch(() => { if (live) toast.error("Failed to load the analysis."); })
        .finally(() => { if (live) setLoading(false); });
    }, 300); // let typing in the wait box settle
    return () => { live = false; clearTimeout(t); };
  }, [from, to, wait, zone, station]);

  const set = (days) => { setFrom(iso(-(days - 1))); setTo(iso()); };

  if (!d) return <div style={S.sub}>{loading ? "Loading…" : ""}</div>;

  const zoneStations = d.filters.stations.filter((s) => !zone || s.zone_id === zone);
  const busiestDay = d.weekdays.reduce((b, w) => (w.avg_issued > (b?.avg_issued || 0) ? w : b), null);
  const hourData = d.hours.map((v, h) => ({ hour: hourLabel(h), v, h })).filter((x) => x.v || (x.h >= 7 && x.h <= 18));
  const peakHour = hourData.reduce((b, x) => (x.v > (b?.v || 0) ? x : b), null);
  const bucketMax = Math.max(1, ...d.wait.buckets.map((b) => b.count));

  return (
    <div style={{ ...S.col, opacity: loading ? 0.6 : 1 }}>
      <div style={S.filters}>
        <label style={S.lab}>From <input type="date" style={S.input} value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} /></label>
        <label style={S.lab}>To <input type="date" style={S.input} value={to} min={from} max={iso()} onChange={(e) => e.target.value && setTo(e.target.value)} /></label>
        {[[7, "7 days"], [14, "14 days"], [30, "30 days"], [90, "90 days"]].map(([n, l]) => <button key={n} style={S.chip} onClick={() => set(n)}>{l}</button>)}
        <select style={S.input} value={zone} onChange={(e) => { setZone(e.target.value); setStation(""); }}>
          <option value="">All zones</option>
          {d.filters.zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
        </select>
        <select style={S.input} value={station} onChange={(e) => setStation(e.target.value)}>
          <option value="">All stations</option>
          {zoneStations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <ExportMenu style={{ marginLeft: "auto" }} report="trends" params={{ date_from: from, date_to: to, wait_minutes: wait || 15, zone, station }} />
      </div>
      <div style={S.sub}>{d.date_from} to {d.date_to} ({d.days} day{d.days !== 1 ? "s" : ""}, longest range 92 days)</div>

      <div style={S.stats}>
        <Stat label="Tickets issued" value={d.totals.issued} tint={P} />
        <Stat label="Patients served" value={d.totals.served} tint="#16A34A" />
        <Stat label="Abandoned" value={d.totals.abandoned} tint={BAD} />
        <Stat label="Average wait" value={mins(d.totals.avg_wait)} tint={A} />
      </div>

      <Card title="Day by day">
        <div style={{ height: 230 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={d.series.map((x) => ({ ...x, day: shortDate(x.date) }))} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid stroke="var(--surface-border)" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
              <Tooltip />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="issued" name="Issued" stroke={A} fill={`${A}33`} strokeWidth={2} />
              <Area type="monotone" dataKey="served" name="Served" stroke={P} fill={`${P}33`} strokeWidth={2} />
              <Area type="monotone" dataKey="abandoned" name="Abandoned" stroke={BAD} fill={`${BAD}22`} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div style={S.row2}>
        <Card title="Busiest days of the week" sub={busiestDay && busiestDay.avg_issued ? `${busiestDay.weekday} is busiest: about ${busiestDay.avg_issued} tickets a day.` : "Average tickets per day, for each weekday in the range."}>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.weekdays.map((w) => ({ ...w, day: w.weekday.slice(0, 3) }))} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
                <YAxis tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
                <Tooltip formatter={(v) => [v, "Tickets per day"]} />
                <Bar dataKey="avg_issued" radius={[3, 3, 0, 0]}>
                  {d.weekdays.map((w) => <Cell key={w.weekday} fill={busiestDay && w.weekday === busiestDay.weekday ? P : `${P}66`} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Busiest hours" sub={peakHour && peakHour.v ? `Peak is ${peakHour.hour.replace("a", " AM").replace("p", " PM")}: about ${peakHour.v} tickets an hour, on average.` : "Average tickets per hour of the day."}>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "var(--text-secondary)" }} interval={0} />
                <YAxis tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
                <Tooltip formatter={(v) => [v, "Tickets per hour"]} />
                <Bar dataKey="v" radius={[3, 3, 0, 0]}>
                  {hourData.map((x) => <Cell key={x.h} fill={peakHour && x.h === peakHour.h ? A : `${A}77`} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card title="How long clients waited" sub="Time from arriving at a station to being called.">
        <div style={S.waitTop}>
          <div style={S.waitBig}>{d.wait.calls ? `${d.wait.within_pct}%` : "–"}</div>
          <div style={{ fontSize: 13, color: "var(--text-primary)" }}>
            of {d.wait.calls} call{d.wait.calls !== 1 ? "s" : ""} were answered within{" "}
            <input type="number" min={1} max={240} style={{ ...S.input, width: 64 }} value={wait} onChange={(e) => setWait(parseInt(e.target.value, 10) || "")} /> minutes
            <div style={S.sub}>Change the number to see the share for another target.</div>
          </div>
        </div>
        {d.wait.buckets.map((b) => (
          <div key={b.label} style={S.bar}>
            <span style={S.barName}>{b.label}</span>
            <div style={S.track}><div style={{ ...S.fill, width: `${(b.count / bucketMax) * 100}%`, background: P }} /></div>
            <span style={S.barVal}>{b.count}</span>
          </div>
        ))}
      </Card>

      <Card title="By station: skipped, held, abandoned and sent back" sub="Skip and hold rates = skipped or held calls out of all calls. Abandon rate = abandoned tickets out of tickets seen. Sent back = a ticket finished at the same station more than once.">
        <table style={S.table}>
          <thead><tr>{["Station", "Tickets", "Calls", "Skipped", "Held", "Abandoned", "Sent back"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {d.stations.map((r) => (
              <tr key={r.station_id}>
                <td style={S.td}><b>{r.station_name}</b><div style={S.sub}>{r.zone_name}</div></td>
                <td style={S.td}>{r.tickets}</td><td style={S.td}>{r.calls}</td>
                <td style={S.td}>{r.skipped} <span style={S.sub}>({r.skip_rate}%)</span></td>
                <td style={S.td}>{r.held} <span style={S.sub}>({r.hold_rate}%)</span></td>
                <td style={S.td}>{r.abandoned} <span style={S.sub}>({r.abandon_rate}%)</span></td>
                <td style={{ ...S.td, color: r.returned ? WARN : undefined }}>{r.returned} <span style={S.sub}>({r.returned_rate}%)</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="Staff throughput" sub="Visits each person finished at their station, and their average service time.">
        {d.staff.length === 0 ? <div style={S.sub}>No finished visits in this range.</div> : (
          <table style={S.table}>
            <thead><tr>{["Staff", "Visits finished", "Active days", "Per active day", "Avg service"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {d.staff.map((r) => (
                <tr key={r.name}>
                  <td style={S.td}><b>{r.name}</b></td><td style={S.td}>{r.served}</td>
                  <td style={S.td}>{r.active_days}</td><td style={S.td}>{r.per_day}</td><td style={S.td}>{mins(r.avg_service)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {d.notes.map((n) => <div key={n} style={S.note}>{n}</div>)}
    </div>
  );
}

const S = {
  col: { display: "flex", flexDirection: "column", gap: 16 },
  sub: { color: "var(--text-secondary)", fontSize: 12, marginTop: 2 },
  big: { fontSize: 28, fontWeight: 700, color: "var(--text-primary)", marginTop: 4 },
  filters: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" },
  lab: { display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-secondary)" },
  input: { background: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--text-primary)", borderRadius: 10, padding: "8px 10px", fontSize: 13, fontFamily: "inherit" },
  chip: { background: `${P}18`, color: P, border: "none", borderRadius: 20, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  stats: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 },
  card: { background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: 14, padding: "16px 18px", boxShadow: "var(--card-shadow)" },
  cardTitle: { fontSize: 14, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 },
  row2: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 },
  waitTop: { display: "flex", alignItems: "center", gap: 18, margin: "10px 0 14px", flexWrap: "wrap" },
  waitBig: { fontSize: 40, fontWeight: 700, color: P, minWidth: 100 },
  bar: { display: "flex", alignItems: "center", gap: 10, margin: "8px 0", fontSize: 13, color: "var(--text-primary)" },
  barName: { width: 120, flexShrink: 0 },
  track: { flex: 1, height: 10, background: "var(--surface-border)", borderRadius: 6, overflow: "hidden" },
  fill: { height: "100%", borderRadius: 6 },
  barVal: { width: 40, textAlign: "right", fontWeight: 600 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, color: "var(--text-primary)", marginTop: 10 },
  th: { textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-secondary)", padding: "8px 10px", borderBottom: "1px solid var(--surface-border)" },
  td: { padding: "10px", borderBottom: "1px solid var(--surface-border)" },
  note: { fontSize: 12, color: "var(--text-secondary)", background: `${WARN}14`, borderRadius: 10, padding: "9px 14px" },
};
